// What the identity page holds of one identity, and how an answer lands in it.
//
// The page stays mounted while it moves from one identity to another — the
// router swaps the id, a panel its selection — and what it asked about the
// identity before may still answer: a slow first read, or a save. So every
// answer says which identity it is about, and one about an identity the page no
// longer shows is dropped. It is matched by identity, not by the order of the
// requests: an answer about the identity before can be the last one to arrive.
import type { IdentityWithDocuments } from './types'

/** The identity a read is of. */
export interface IdentityRef {
  caseId: string
  identityId: string
}

export function sameIdentity(a: IdentityRef, b: IdentityRef): boolean {
  return a.caseId === b.caseId && a.identityId === b.identityId
}

export interface IdentityRead extends IdentityRef {
  data: IdentityWithDocuments | null
  /** Until the identity's first read answers; a re-read keeps the page on screen. */
  isLoading: boolean
  /**
   * What the last read failed with, and when: the shape a query keeps its
   * failure in, so that both are asked the same things (`isGoneForGood`,
   * `survivorNamedBy`).
   */
  error: unknown
  errorUpdatedAt: number
  /** The identity's graph view is being put together. */
  openingGraph: boolean
}

/** What happened to a request about an identity. */
export type IdentityReadEvent =
  | { type: 'started' }
  | { type: 'loaded'; data: IdentityWithDocuments }
  | { type: 'failed'; error: unknown; at: number }
  /** An inline edit was saved; the answer is the identity as it now stands. */
  | { type: 'saved'; data: IdentityWithDocuments }
  | { type: 'graph'; opening: boolean }

/** The read of an identity nothing has answered yet. */
export function unread({ caseId, identityId }: IdentityRef): IdentityRead {
  return {
    caseId,
    identityId,
    data: null,
    isLoading: true,
    error: null,
    errorUpdatedAt: 0,
    openingGraph: false,
  }
}

/** *read* with *patch* applied, or *read* itself when the patch changes nothing. */
function patched(read: IdentityRead, patch: Partial<IdentityRead>): IdentityRead {
  const keys = Object.keys(patch) as (keyof IdentityRead)[]
  return keys.every(key => read[key] === patch[key]) ? read : { ...read, ...patch }
}

/**
 * *read* after *event*, which is about the identity *about*. An event about any
 * other identity changes nothing.
 */
export function afterEvent(
  read: IdentityRead,
  about: IdentityRef,
  event: IdentityReadEvent
): IdentityRead {
  if (!sameIdentity(read, about)) return read
  switch (event.type) {
    case 'started':
      return patched(read, { error: null })
    case 'loaded':
      return patched(read, { data: event.data, isLoading: false, error: null })
    case 'failed':
      return patched(read, { isLoading: false, error: event.error, errorUpdatedAt: event.at })
    case 'saved':
      return patched(read, { data: event.data })
    case 'graph':
      return patched(read, { openingGraph: event.opening })
  }
}
